import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import preferences from 'Preferences';
import {StudiesPreferences} from 'studies/StudiesPreferences';

/**
 * Gates the flowchart-based procedure run / run plan design UI.
 *
 * Always use this helper rather than directly accessing BenchlingConfig or preferences.
 */
export function shouldShowFlowchartProcedureRunDesign(): boolean {
  return (
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN &&
    preferences.get(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN)
  );
}

/**
 * Controls whether the flowchart opt-in banner should be shown in legacy run/run-plan design.
 */
export function shouldShowFlowchartProcedureRunDesignOptInBanner(): boolean {
  return (
    BenchlingConfig.STUDIES_ENABLE_FLOWCHART_PROCEDURE_RUN_DESIGN &&
    !preferences.get(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN)
  );
}

/**
 * Opts the current user into the flowchart run/run-plan design.
 */
export async function optInToFlowchartProcedureRunDesign(): Promise<void> {
  await preferences.set(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN, true);
}

/**
 * Opts out of the flowchart run/run-plan design and selects a fallback method, since the legacy UI
 * requires a non-null selected method id.
 *
 */
export async function optOutOfFlowchartProcedureRunDesign(params: {
  selectedMethodId: string | null;
  fallbackMethodId: string | undefined;
  setSelectedMethodId: (methodId: string) => void;
  onOptedOut: () => void;
}): Promise<void> {
  const methodIdToSelect = params.selectedMethodId ?? params.fallbackMethodId;
  assert(
    methodIdToSelect != null,
    'Expected a fallback method id to be non-null before opting out of the flowchart design'
  );
  params.setSelectedMethodId(methodIdToSelect);
  await preferences.set(StudiesPreferences.SHOW_FLOWCHART_PROCEDURE_RUN_DESIGN, false);
  params.onOptedOut();
}
